import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  Layout,
  List,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { api } from '../services/api'
import './Tools.css'

const { Header, Content } = Layout
const { Title, Paragraph, Text } = Typography
const { Search } = Input
const { TextArea } = Input

type SearchType = 'keyword' | 'semantic' | 'hybrid'

interface ToolItem {
  id: number
  name: string
  description: string
  links: Array<{ url: string; type?: string; description?: string }>
  tags: Array<{ id: number; name: string; color?: string }>
  source_article_id?: number
  source_system: string
  is_favorite?: boolean
  is_archived?: boolean
  similarity_score?: number
  search_score?: number
}

interface TagOption {
  id: number
  name: string
  color?: string
}

const Tools: React.FC = () => {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [searchType, setSearchType] = useState<SearchType>('hybrid')
  const [items, setItems] = useState<ToolItem[]>([])
  const [loading, setLoading] = useState(false)
  const [analyzingId, setAnalyzingId] = useState<number | null>(null)
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([])
  const [tags, setTags] = useState<TagOption[]>([])

  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editingTool, setEditingTool] = useState<ToolItem | null>(null)
  const [editForm] = Form.useForm()
  const [editSubmitting, setEditSubmitting] = useState(false)

  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [createForm] = Form.useForm()
  const [createSubmitting, setCreateSubmitting] = useState(false)

  const loadTags = async () => {
    try {
      const res: any = await api.tags.list()
      if (res.success) setTags(res.data.items || [])
    } catch (e) {
      console.error('加载标签失败', e)
    }
  }

  const loadTools = async (searchQuery?: string) => {
    const q = (searchQuery ?? query).trim()
    setLoading(true)
    try {
      const res: any = q
        ? await api.tools.search({ q, search_type: searchType, limit: 20 })
        : await api.tools.list({ page: 1, size: 20 })
      if (res.success) {
        setItems((res.data || {}).items || [])
      }
    } catch (e) {
      console.error('加载工具失败', e)
      message.error('加载工具失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTags()
    loadTools()
  }, [searchType])

  const handleReanalyze = async (toolId: number) => {
    try {
      setAnalyzingId(toolId)
      message.loading({ content: '正在重新分析…', key: 'reanalyze', duration: 0 })
      const res: any = await api.tools.reanalyze(toolId)
      if (res.success) {
        message.success({ content: '已重新分析', key: 'reanalyze', duration: 2 })
        setItems(prev => prev.map(i => (i.id === toolId ? { ...i, ...res.data } : i)))
      }
    } catch (e) {
      console.error('重新分析失败', e)
      message.error({ content: '重新分析失败', key: 'reanalyze', duration: 2 })
    } finally {
      setAnalyzingId(null)
    }
  }

  const handleDelete = async (toolId: number) => {
    try {
      await api.tools.delete(toolId)
      message.success('已删除')
      setItems(prev => prev.filter(i => i.id !== toolId))
    } catch (e) {
      console.error('删除失败', e)
      message.error('删除失败')
    }
  }

  const handleBatchDelete = async () => {
    const ids = selectedIds as number[]
    try {
      await Promise.all(ids.map(id => api.tools.delete(id)))
      message.success(`已删除 ${ids.length} 个工具`)
      setSelectedIds([])
      loadTools()
    } catch (e) {
      console.error('批量删除失败', e)
      message.error('批量删除部分失败')
      loadTools()
    }
  }

  const openEdit = (tool: ToolItem) => {
    setEditingTool(tool)
    editForm.setFieldsValue({
      name: tool.name,
      description: tool.description,
      tags: tool.tags.map(t => t.name),
      links: tool.links.map(l => l.url).join('\n'),
    })
    setEditModalVisible(true)
  }

 const handleEditSubmit = async (values: any) => {
    if (!editingTool) return
    try {
      setEditSubmitting(true)
      const linkLines = (values.links || '').split('\n').map((s: string) => s.trim()).filter(Boolean)
      const links = linkLines.map((url: string) => ({ url, type: '相关链接', description: '' }))
      await api.tools.update(editingTool.id, {
        name: values.name,
        description: values.description,
        tags: values.tags || [],
        links,
      })
      message.success('已更新')
      setEditModalVisible(false)
      loadTools()
    } catch (e) {
      console.error('更新失败', e)
      message.error('更新失败')
    } finally {
      setEditSubmitting(false)
    }
  }

  const handleCreateSubmit = async (values: any) => {
    try {
      setCreateSubmitting(true)
      const linkLines = (values.links || '').split('\n').map((s: string) => s.trim()).filter(Boolean)
      const links = linkLines.map((url: string) => ({ url, type: '相关链接', description: '' }))
      const res: any = await api.tools.create({
        name: values.name,
        description: values.description || '',
        tags: values.tags || [],
        links,
      })
      if (res.success) {
        message.success('工具已创建')
        setCreateModalVisible(false)
        createForm.resetFields()
        loadTools()
      }
    } catch (e) {
      console.error('创建失败', e)
      message.error('创建失败')
    } finally {
      setCreateSubmitting(false)
    }
  }

  return (
    <Layout className="tools-layout">
      <Header className="tools-header">
        <Space>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} className="tools-back">返回</Button>
          <ToolOutlined className="tools-title-icon" />
          <Title level={3} style={{ color: 'white', margin: 0 }}>工具库</Title>
        </Space>
      </Header>

      <Content className="tools-content">
        <Card className="tools-search-card">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Search
                value={query}
                onChange={e => setQuery(e.target.value)}
                onSearch={loadTools}
                enterButton={<><SearchOutlined /> 搜索工具</>}
                size="large"
                placeholder="按功能、场景、名称或标签搜索工具"
                allowClear
                style={{ width: 500 }}
              />
              <Space>
                {selectedIds.length > 0 && (
                  <Popconfirm title={`删除选中的 ${selectedIds.length} 个工具？`} onConfirm={handleBatchDelete}>
                    <Button danger icon={<DeleteOutlined />}>批量删除</Button>
                  </Popconfirm>
                )}
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>新建工具</Button>
              </Space>
            </Space>
            <Radio.Group value={searchType} onChange={e => setSearchType(e.target.value)} optionType="button" buttonStyle="solid">
              <Radio.Button value="keyword">关键词</Radio.Button>
              <Radio.Button value="semantic">语义</Radio.Button>
              <Radio.Button value="hybrid">混合搜索</Radio.Button>
            </Radio.Group>
          </Space>
        </Card>

        <Spin spinning={loading}>
          {items.length === 0 ? (
            <Empty description="还没有匹配的工具" />
          ) : (
            <List
              grid={{ gutter: 16, xs: 1, sm: 1, md: 2, xl: 3 }}
              dataSource={items}
              renderItem={tool => (
                <List.Item>
                  <Card
                    className="tool-card"
                    title={<Space><ToolOutlined /><span>{tool.name}</span></Space>}
                    extra={
                      <Space size={4}>
                        {tool.search_score !== undefined && <Text type="secondary">{tool.search_score.toFixed(3)}</Text>}
                        <Tooltip title="编辑">
                          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(tool)} />
                        </Tooltip>
                        <Popconfirm title="删除这个工具？" onConfirm={() => handleDelete(tool.id)}>
                          <Tooltip title="删除">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    }
                  >
                    <Paragraph ellipsis={{ rows: 3 }}>{tool.description || '暂无描述'}</Paragraph>
                    <Space size={[4, 8]} wrap className="tool-tags">
                      {tool.tags.map(tag => (<Tag key={tag.id} color={tag.color}>{tag.name}</Tag>))}
                    </Space>
                    <div className="tool-links">
                      {tool.links.slice(0, 3).map(link => (
                        <Button key={link.url} type="link" size="small" icon={<LinkOutlined />} href={link.url} target="_blank">
                          {link.type || '打开链接'}
                        </Button>
                      ))}
                    </div>
                    <Space>
                      {tool.source_article_id && (
                        <Button type="link" onClick={() => navigate(`/articles/${tool.source_article_id}`)}>查看来源</Button>
                      )}
                      {tool.source_article_id && (
                        <Tooltip title="用 AI 重新分析来源文章并更新此工具">
                          <Button type="link" icon={<ReloadOutlined />} loading={analyzingId === tool.id} onClick={() => handleReanalyze(tool.id)}>重新分析</Button>
                        </Tooltip>
                      )}
                    </Space>
                  </Card>
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Content>

      {/* 编辑弹窗 */}
      <Modal title="编辑工具" open={editModalVisible} onCancel={() => setEditModalVisible(false)} footer={null} width={600} destroyOnClose>
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入工具名称' }]}>
            <Input placeholder="工具名称" />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <TextArea placeholder="工具描述" rows={4} maxLength={2000} showCount />
          </Form.Item>
          <Form.Item label="标签" name="tags">
            <Select mode="multiple" placeholder="选择或输入标签" allowClear>
              {tags.map(t => (<Select.Option key={t.id} value={t.name}><Tag color={t.color}>{t.name}</Tag></Select.Option>))}
            </Select>
          </Form.Item>
          <Form.Item label="链接（每行一个URL）" name="links">
            <TextArea placeholder="https://..." rows={3} />
          </Form.Item>
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={editSubmitting}>保存</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 新建弹窗 */}
      <Modal title="新建工具" open={createModalVisible} onCancel={() => setCreateModalVisible(false)} footer={null} width={600} destroyOnClose>
        <Form form={createForm} layout="vertical" onFinish={handleCreateSubmit}>
          <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入工具名称' }]}>
            <Input placeholder="工具名称" />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <TextArea placeholder="工具描述（可选）" rows={4} maxLength={2000} showCount />
          </Form.Item>
          <Form.Item label="标签" name="tags">
            <Select mode="multiple" placeholder="选择或输入标签" allowClear>
              {tags.map(t => (<Select.Option key={t.id} value={t.name}><Tag color={t.color}>{t.name}</Tag></Select.Option>))}
            </Select>
          </Form.Item>
          <Form.Item label="链接（每行一个URL）" name="links">
            <TextArea placeholder="https://..." rows={3} />
          </Form.Item>
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setCreateModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={createSubmitting} icon={<PlusOutlined />}>创建</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}

export default Tools
